// React
import React, {
  Children,
  useState,
  useCallback,
  useEffect,
  useMemo,
  isValidElement,
  cloneElement,
} from "react";
import PropTypes from "prop-types";
// Helpers
import { isEmpty } from "@mefisto/utils";
// Framework
import { classnames } from "ui/classnames";
import { makeStyles, TableCell } from "ui/components";
import { TableRow as TableRowComponent } from "@material-ui/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// Components
import TableRowOptions from "./components/TableRowOptions";
import TableRowDragHandle from "./components/TableRowDragHandle";
import { useTableContext } from "../TableContext";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles((theme) => ({
  root: {
    height: 56,
    backgroundColor: theme.palette.background.default,
    borderColor: "transparent",
  },
  highlighted: {
    borderColor: theme.palette.border.surface.color,
    backgroundColor: theme.palette.background.surface.main,
  },
  selected: {
    borderColor: theme.palette.border.surface.color,
    backgroundColor: theme.palette.background.surface.main,
  },
  selectable: {
    cursor: "pointer",
  },
  badge: {
    position: "sticky",
    left: 0,
    padding: 0,
    borderLeft: "6px solid",
    borderRadius: `0 ${theme.radius.normal} ${theme.radius.normal} 0`,
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const TableRow = ({
  selectable,
  selected,
  badge,
  options,
  draggable,
  children,
  onSelection,
  __renderProps: { key, index, dragged = false } = {},
}) => {
  // Styles
  const classes = useStyles();
  // Draggable
  const {
    attributes,
    listeners,
    transform,
    transition,
    setNodeRef,
    isDragging,
    activeIndex,
  } = useSortable({
    id: key ?? index,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide row when dragging, we use DragOverlay to show the dragging item
    ...(isDragging ? { visibility: "hidden" } : {}),
  };
  // Context
  const { data, setPrepend, setAppend } = useTableContext();
  // State
  const [highlighted, setHighlighted] = useState(dragged);
  // Memo
  const hasOptions = useMemo(() => {
    return !isEmpty(options);
  }, [options]);
  // Effect
  useEffect(() => {
    if (badge) {
      setPrepend(true);
    }
    if (hasOptions) {
      setAppend(true);
    }
  }, [setAppend, setPrepend, badge, hasOptions]);
  // Callbacks
  const handleClick = useCallback(() => {
    selectable && onSelection && onSelection(data[index], index);
  }, [selectable, onSelection, data, index]);
  const handleMouseOver = useCallback(() => {
    if (activeIndex === -1) {
      setHighlighted(true);
    } else if (selectable || hasOptions) {
      setHighlighted(true);
    }
  }, [activeIndex, selectable, hasOptions]);
  const handleMouseLeave = useCallback(() => {
    if (selectable || hasOptions) {
      setHighlighted(false);
    }
  }, [selectable, hasOptions]);
  // Render
  return (
    <TableRowComponent
      ref={setNodeRef}
      tabIndex={-1}
      className={classnames(classes.root, {
        [classes.highlighted]: highlighted,
        [classes.selected]: selected,
        [classes.selectable]: selectable,
      })}
      style={style}
      onClick={!dragged ? handleClick : undefined}
      onMouseOver={!dragged ? handleMouseOver : undefined}
      onMouseLeave={!dragged ? handleMouseLeave : undefined}
    >
      {badge && (
        <TableCell
          className={classes.badge}
          style={{ borderLeftColor: badge }}
        />
      )}
      {(draggable || dragged) && (
        <TableRowDragHandle
          highlighted={highlighted}
          attributes={attributes}
          listeners={listeners}
          isDragging={isDragging}
        />
      )}
      {Children.map(
        children,
        (child, index) =>
          isValidElement(child) &&
          cloneElement(child, {
            __renderProps: {
              index,
              draggable,
              dragged,
              selected,
              highlighted,
            },
          })
      )}
      {options && (
        <TableRowOptions
          index={index}
          highlighted={highlighted}
          options={options}
        />
      )}
    </TableRowComponent>
  );
};

TableRow.propTypes = {
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  badge: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.node),
  children: PropTypes.node,
  onSelection: PropTypes.func,
};

export default TableRow;
